import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type HorizontalAlign = 'start' | 'center' | 'end';

interface AnchoredPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  align?: HorizontalAlign;
  offset?: number;
  className?: string;
  children: React.ReactNode;
}

interface PopoverPosition {
  left: number;
  top: number;
}

const VIEWPORT_PADDING = 12;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const AnchoredPopover: React.FC<AnchoredPopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
  align = 'end',
  offset = 12,
  className = '',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) {
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();

      let nextLeft = anchorRect.left;
      if (align === 'center') {
        nextLeft = anchorRect.left + (anchorRect.width / 2) - (panelRect.width / 2);
      } else if (align === 'end') {
        nextLeft = anchorRect.right - panelRect.width;
      }

      const maxLeft = window.innerWidth - panelRect.width - VIEWPORT_PADDING;
      const left = clamp(nextLeft, VIEWPORT_PADDING, Math.max(VIEWPORT_PADDING, maxLeft));

      const fitsBelow = anchorRect.bottom + offset + panelRect.height <= window.innerHeight - VIEWPORT_PADDING;
      const top = fitsBelow
        ? anchorRect.bottom + offset
        : Math.max(VIEWPORT_PADDING, anchorRect.top - panelRect.height - offset);

      setPosition({ left, top });
    };

    updatePosition();

    const handleViewportChange = () => {
      window.requestAnimationFrame(updatePosition);
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [align, anchorRef, isOpen, offset]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      if (panel?.contains(target) || anchor?.contains(target)) {
        return;
      }

      onClose();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [anchorRef, isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`fixed z-[1200] ${className}`}
      style={{
        left: position?.left ?? VIEWPORT_PADDING,
        top: position?.top ?? VIEWPORT_PADDING,
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default AnchoredPopover;
